// XSM-1536 - SSO Login flow
// Author: Abhishek Bharti
import { ReactNode, useMemo, useState, useEffect, useRef } from "react";

import styles from "./sidebar.module.scss";
import { ProductListItem, useProductList } from "../../../hooks/useProductList";
import GroupSVG from "../../../assets/svg/group.svg";
import ArrowIcon from "src/modules/atoms/ArrowIcon";

export interface SidebarItem {
  id: string;
  label?: string;
  url?: string;
  icon?: ReactNode;
}

interface SidebarProps {
  items: SidebarItem[];
  selectedItem: string;
  onSelect: (item: SidebarItem) => void;
  logo?: ReactNode;
  onLogout: () => void;
  logoutLabel?: string;
  productOptions?: ProductListItem[];
}

const SidebarWithProductDropdown = ({
  items,
  selectedItem,
  onSelect,
  onLogout,
  logoutLabel = "Exit",
  productOptions,
}: SidebarProps) => {
  const { products } = useProductList();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isDropdownOpen]);

  const productList = useMemo<ProductListItem[]>(() => {
    if (products.length > 0) {
      return products;
    }
    if (productOptions && productOptions.length > 0) {
      return productOptions;
    }
    return [];
  }, [products, productOptions]);

  const handleProductClick = (product: ProductListItem) => {
    if (product.link) {
      window.open(product.link, "_self");
    }
  };

  const handleItemClick = (item: SidebarItem) => {
    onSelect(item);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <aside className={styles.sidebar} ref={dropdownRef}>
      <div className={styles.topSection}>
        <div className={styles.logoContainer}>
          DEVICE MANAGER
          {productList?.length > 0 && (
            <>
              <img
                src={GroupSVG}
                alt="group icon"
                width={"20px"}
                height={"20px"}
                style={{ margin: "0 4px" }}
              />
              <button
                type="button"
                className={styles.dropdownToggle}
                onClick={toggleDropdown}
                aria-label="Toggle product dropdown"
              >
                <ArrowIcon
                  style={{
                    transform: isDropdownOpen
                      ? "rotate(0deg)"
                      : "rotate(180deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </button>
            </>
          )}
        </div>
        {productList.length > 0 && isDropdownOpen ? (
          <div className={styles.productCards}>
            {productList.map((product) => (
              <button
                key={product.id}
                type="button"
                className={styles.productCard}
                onClick={() => handleProductClick(product)}
              >
                <div className={styles.productIconContainer}>
                  <img
                    src={product.icon}
                    alt={product.name}
                    className={styles.productIcon}
                  />
                </div>
                <span className={styles.productLabel}>{product.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <nav className={styles.nav}>
        {items.map((item) => (
          <button
            type="button"
            key={item.id}
            className={`${styles.navItem} ${selectedItem === item.id ? styles.active : ""
              }`}
            onClick={() => handleItemClick(item)}
          >
            {item.icon ? (
              <span className={styles.icon}>{item.icon}</span>
            ) : null}
            <span className={styles.label}>{item.label ?? item.id}</span>
          </button>
        ))}
      </nav>

      <div className={styles.logoutSection}>
        <button
          id="sidebar-logout-btn"
          type="button"
          className={styles.logoutButton}
          onClick={onLogout}
        >
          {logoutLabel}
        </button>
      </div>
    </aside>
  );
};

export default SidebarWithProductDropdown;
